import axios from 'axios';
import React, { useState,useEffect ,useRef,useCallback} from 'react'
import { useNavigate } from 'react-router-dom';
import ResearcherLikeCommentActions from './IndependentResearcher/ResearcherLikeCommentActions';
import MarketplaceFeedDetailsModal from './MarketPlaceFeedDetailsModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const MarketPlaceFeedHome = ({ searchParams }) => {
  const navigate = useNavigate();
  const [feedPosts, setFeedPosts] = useState([]);
  const searchTerm = searchParams.get("search") || "";
  // console.log("The search term is:",searchParams)
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");
  const [searchType, setSearchType] = useState("");
  const [postId, setPostId] = useState(null);
  const [open, setOpen] = useState(false);

  const pageRef = useRef(1);
  const observer = useRef(null);
  const prevSearchTermRef = useRef(searchTerm);

  // ---------------- FETCH POSTS ----------------
  const fetchFeedPosts = async (pageNum, isNewSearch = false) => {
    if (loading) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${BASE_URL}/marketplace/researcher/feed/?page=${pageNum}`,
        {
          params: {
            q: searchTerm,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'x-api-key': process.env.REACT_APP_X_API_KEY,
          },
        }
      );

      const newData = response.data.results;

      setFeedPosts((prev) =>
        isNewSearch || pageNum === 1 ? newData : [...prev, ...newData]
      );

      setHasMore(!!response.data.next);
      pageRef.current = pageNum + 1;

    } catch (error) {
      console.error('Error fetching feed posts:', error);
      if (pageNum === 1) {
        setFeedPosts([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RESET AND FETCH ON SEARCH CHANGE ----------------
  useEffect(() => {
    // Reset everything when search term changes
    if (prevSearchTermRef.current !== searchTerm) {
      setFeedPosts([]);
      setHasMore(true);
      pageRef.current = 1;
      
      // Disconnect observer if it exists
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
      
      // Fetch with new search term
      fetchFeedPosts(1, true);
      prevSearchTermRef.current = searchTerm;
    }
  }, [searchTerm]);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchFeedPosts(1, true);
  }, []);

  // ---------------- LAST POST OBSERVER ----------------
  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loading) {
            fetchFeedPosts(pageRef.current, false);
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, searchTerm] // Added searchTerm dependency
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);
  return (
    <>
      {/* Empty state for no results */}
      {!loading && feedPosts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">
            {searchTerm ? `No results found for "${searchTerm}"` : "No posts available"}
          </div>
        </div>
      )}

      {feedPosts.map((post, index) => {
        const isLast = index === feedPosts.length - 1;

        return (
          <div
            key={`${post.id}-${post.research_type}`} // Better unique key
            ref={isLast ? lastPostRef : null}
            onClick={() => {
              window.history.pushState(
                {},
                "",
                `/marketplace/independent_researcher/post/${post.id}?research_type=${post.research_type}&type=${post.publication_type}`
              );

              setType(post?.publication_type);
              setSearchType(post?.research_type);
              setPostId(post?.id);
              setOpen(true);
            }}
            className="mb-4 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className="p-6 w-full cursor-pointer">
              {/* USER INFO */}
              <div className="flex items-center gap-3 justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={post?.profile_data?.profile_picture}
                    alt="User"
                    className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"
                    onError={(e) => {
                      e.target.src = '/default-avatar.png'; // Add fallback
                    }}
                  />

                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">
                      {post?.profile_data?.name}
                    </span>
                    <span className="text-sm text-gray-500">{post.date}</span>
                  </div>
                </div>
              </div>

              {/* TAGS */}
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs px-3 py-1 bg-gray-100 rounded-lg">
                  {post?.field_of_interest}
                </span>
                <span className="text-xs px-3 py-1 bg-blue-100 rounded-lg">
                  {post?.area_of_interest}
                </span>
              </div>

              {/* TITLE */}
              <div className="text-lg font-bold text-gray-900 mb-2">
                {post.name}
              </div>

              {/* MEDIA */}
              {post.publication_type === "image" && (
                <img 
                  src={post.file} 
                  className="w-full h-48 object-cover rounded-lg" 
                  alt={post.name}
                />
              )}

              {post.publication_type === "video" && (
                <video 
                  src={post.file} 
                  controls 
                  className="w-full h-48 rounded-lg" 
                />
              )}

              {post.publication_type === "audio" && (
                <audio 
                  src={post.file} 
                  controls 
                  className="w-full mt-2" 
                />
              )}

              {post.publication_type === "pdf" && (
                <iframe
                  src={`${post.file}#toolbar=0`}
                  className="w-full h-48 rounded-lg"
                  title={post.name}
                />
              )}

              {/* ACTIONS */}
              <ResearcherLikeCommentActions
                postId={post?.id}
                type={post?.research_type}
                is_Liked={post?.is_liked}
                likeCount={post?.like_count}
                views={post?.views}
              />
            </div>
          </div>
        );
      })}

      {/* LOADING STATE */}
      {loading && (
        <div className="flex justify-center py-6">
          <div className="text-gray-500 text-sm">Loading...</div>
        </div>
      )}

      {/* Load more indicator */}
      {!loading && hasMore && feedPosts.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">Scroll to load more</div>
        </div>
      )}

      {/* End of list indicator */}
      {!loading && !hasMore && feedPosts.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">No more posts</div>
        </div>
      )}

      {/* MODAL */}
      <MarketplaceFeedDetailsModal
        id={postId}
        type={type}
        research_type={searchType}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

export default React.memo(MarketPlaceFeedHome)
